import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, RefreshCw, ShieldCheck } from "lucide-react";

import {
  ExpiryMismatchTable,
  HowThisWorks,
  ResourcesWithoutCycleTable,
} from "@/components/billing-cycles";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getPaginationItems } from "@/lib/pagination";
import type { PaginationInfo } from "@/types/api-response";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useExpiryMismatches,
  useResourcesWithoutCycle,
} from "@/hooks/useBillingCycleQueries";

type ReportTab = "drift" | "no-cycle";

/** Rows per page, matching the size the other admin tables open on. */
const PAGE_SIZE_DEFAULT = 20;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * The billing-consistency worklist, in two tabs because they are two different questions about the
 * same pair of records — and neither report can express the other:
 *
 *  1. **Drift** — a machine and its ACTIVE cycle both exist and name different expiry dates. The
 *     same pairs `ResourceExpiryReconciliationJob` repairs on its nightly run.
 *  2. **No billing cycle** — a machine still INSIDE its term with no active cycle at all: served
 *     right now on something nobody is billing. Comparing two dates needs both, so these drop out
 *     of the drift query rather than becoming a finding there, and nothing repairs them either —
 *     the nightly job only moves a resource onto a cycle that exists.
 *
 * LIVE machines only, in both. Cycles left open on a deleted machine used to be listed beside the
 * drift and were dropped — an operator cannot act on a machine that is gone, and those rows buried
 * the findings that are actually actionable.
 *
 * Its own page rather than a view inside the cycle list, because it is a worklist: an operator opens
 * a row, fixes it on the machine's expiry screen and comes back, and a plain route is what makes the
 * browser's Back button land them here rather than on the full cycle table.
 *
 * Read-only. Repairing is a decision made on the expiry screen, where both dates are editable side
 * by side — the cycle is the record a confirmed payment produced, but a manual grant lives on the
 * resource, so neither side is automatically the right one.
 */
export function ExpiryMismatchPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ReportTab>("drift");

  // A page per tab, not one shared number: the two reports are different lengths, so carrying page
  // 4 across a tab switch would land the operator on an empty page of the other one.
  const [driftPage, setDriftPage] = useState(1);
  const [noCyclePage, setNoCyclePage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  // Both load on arrival rather than on tab switch: the counts belong on the tab labels, and a
  // count that only appears once you click the tab cannot tell you whether to click it.
  const drift = useExpiryMismatches(true, driftPage, pageSize);
  const noCycle = useResourcesWithoutCycle(true, noCyclePage, pageSize);

  const driftRows = drift.data?.data ?? [];
  const noCycleRows = noCycle.data?.data ?? [];

  // The count on the tab label is the whole report, not the page — an operator deciding whether to
  // open a tab wants to know how much is in it, and "12" beside a 20-row page would be a lie.
  const driftTotal = drift.data?.pagination.totalItems ?? 0;
  const noCycleTotal = noCycle.data?.pagination.totalItems ?? 0;

  const active = tab === "drift" ? drift : noCycle;

  // Any page-size change restarts both reports at page 1: keeping the old page number under a new
  // size points at a different slice of rows, or past the end of the shorter report entirely.
  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    setDriftPage(1);
    setNoCyclePage(1);
  };

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="flex min-h-0 flex-1 flex-col shadow-none">
        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold">Billing consistency</h1>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <Button
                onClick={() => void active.refetch()}
                variant="outline"
                size="sm"
                disabled={active.isFetching}
                className="h-9 min-h-9 gap-2 px-3 text-sm hover:translate-y-0 active:translate-y-0"
              >
                <RefreshCw
                  className={`h-4 w-4 ${active.isFetching ? "animate-spin" : ""}`}
                />
                Check again
              </Button>

              <Button
                onClick={() => navigate("/admin/billing-cycles")}
                variant="outline"
                size="sm"
                className="h-9 min-h-9 cursor-pointer gap-2 px-3 text-sm hover:translate-y-0 active:translate-y-0"
              >
                <ArrowLeft className="h-4 w-4" />
                Billing cycles
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          <Tabs
            value={tab}
            onValueChange={(next) => setTab(next as ReportTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            {/* One row: the tabs pick the report, the link explains the one being read. The guide
                anchor follows the active tab, so the button always lands on the section for what is
                on screen. */}
            <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
              <TabsList>
                {/* No count at all until `data` exists — the first load and a failed check both
                    leave it undefined, and "(0)" during either reads as a confirmed answer that
                    hasn't actually arrived yet. */}
                <TabsTrigger value="drift" className="cursor-pointer">
                  {drift.data ? `Expiry drift (${driftTotal})` : "Expiry drift"}
                </TabsTrigger>
                <TabsTrigger value="no-cycle" className="cursor-pointer">
                  {noCycle.data ? `No billing cycle (${noCycleTotal})` : "No billing cycle"}
                </TabsTrigger>
              </TabsList>

              <HowThisWorks anchor={tab === "drift" ? "drift" : "no-cycle"} />
            </div>

            <TabsContent
              value="drift"
              className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
            >
              {/* The full-screen ReportError is reached only when there is genuinely nothing to
                  show — `driftRows.length === 0` in the same branch as `drift.error`. A failed
                  "Check again" on a page that still has rows (from `placeholderData` carrying the
                  last successful page forward) used to hit this branch too and threw the table away
                  along with it; that case is now the inline banner below instead. */}
              {drift.error && driftRows.length === 0 ? (
                <ReportError
                  message="Failed to check expiry mismatches"
                  onRetry={() => void drift.refetch()}
                  isFetching={drift.isFetching}
                />
              ) : drift.isFetching && driftRows.length === 0 ? (
                <LoadingRows />
              ) : driftRows.length === 0 ? (
                <EmptyReport
                  title="Every active cycle matches its machine"
                  detail="No machine is running on a date its billing cycle disagrees with. The nightly job has no work to do either."
                />
              ) : (
                <>
                  {drift.error && (
                    <RefreshErrorBanner
                      onRetry={() => void drift.refetch()}
                      isFetching={drift.isFetching}
                    />
                  )}
                  <ExpiryMismatchTable rows={driftRows} />
                </>
              )}
              {/* Outside the three branches above, on purpose: a page repaired down to zero rows
                  still needs the way back to an earlier page, and `ReportPagination` already
                  renders nothing when there is no pagination to show (the true first load, or a
                  failure with nothing cached yet) — see its own doc comment. */}
              <ReportPagination
                pagination={drift.data?.pagination}
                page={driftPage}
                pageSize={pageSize}
                isFetching={drift.isFetching}
                onPageChange={setDriftPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </TabsContent>

            <TabsContent
              value="no-cycle"
              className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
            >
              {noCycle.error && noCycleRows.length === 0 ? (
                <ReportError
                  message="Failed to check resources without a billing cycle"
                  onRetry={() => void noCycle.refetch()}
                  isFetching={noCycle.isFetching}
                />
              ) : noCycle.isFetching && noCycleRows.length === 0 ? (
                <LoadingRows />
              ) : noCycleRows.length === 0 ? (
                <EmptyReport
                  title="Every machine in term has an active cycle"
                  detail="Nothing is being served on a term with no billing record behind it."
                />
              ) : (
                <>
                  {noCycle.error && (
                    <RefreshErrorBanner
                      onRetry={() => void noCycle.refetch()}
                      isFetching={noCycle.isFetching}
                    />
                  )}
                  <ResourcesWithoutCycleTable rows={noCycleRows} />
                </>
              )}
              <ReportPagination
                pagination={noCycle.data?.pagination}
                page={noCyclePage}
                pageSize={pageSize}
                isFetching={noCycle.isFetching}
                onPageChange={setNoCyclePage}
                onPageSizeChange={handlePageSizeChange}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The footer both tabs share: page position, page size and the page buttons.
 *
 * Rendered at every page count, including one. Hiding the buttons on a single-page report made the
 * controls appear and disappear as the page size changed — the one setting that decides whether a
 * second page exists at all — so the row an operator was aiming at moved under them. Previous and
 * Next disable themselves at the ends, so a one-page report simply shows both greyed out next to
 * page 1, which says "this is all of it" rather than leaving the question open. Same rule as the
 * billing-cycle and payment lists.
 */
function ReportPagination({
  pagination,
  page,
  pageSize,
  isFetching,
  onPageChange,
  onPageSizeChange,
}: {
  pagination?: PaginationInfo;
  page: number;
  pageSize: number;
  isFetching: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) {
  if (!pagination) {
    return null;
  }

  const { totalPages, totalItems } = pagination;

  return (
    <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages} ({totalItems} total)
      </p>

      <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
        >
          <SelectTrigger className="h-8 w-[120px] cursor-pointer text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size} per page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 overflow-x-auto">
          <Button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1 || isFetching}
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs"
          >
            Previous
          </Button>

          {getPaginationItems(page, totalPages).map((item, index) =>
            item === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="px-1 text-sm text-muted-foreground"
              >
                …
              </span>
            ) : (
              <Button
                key={item}
                onClick={() => onPageChange(item as number)}
                disabled={isFetching}
                variant={page === item ? "default" : "outline"}
                size="sm"
                className="h-8 min-w-[32px] px-2 text-xs"
              >
                {item}
              </Button>
            ),
          )}

          <Button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages || isFetching}
            variant="outline"
            size="sm"
            className="h-8 px-2 text-xs"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function LoadingRows() {
  // min-h-0 flex-1 on all three non-table branches: the pager now sits outside them with
  // `mt-auto`, so a branch sized to its own content let the footer jump up under a short message
  // the moment "Check again" emptied the page, and the card changed height under the operator.
  return (
    <div className="min-h-0 flex-1 space-y-2">
      <Skeleton className="h-10" />
      <Skeleton className="h-10" />
      <Skeleton className="h-10" />
    </div>
  );
}

function ReportError({
  message,
  onRetry,
  isFetching,
}: {
  message: string;
  onRetry: () => void;
  isFetching: boolean;
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
      <p className="text-destructive-ink">{message}</p>
      <Button onClick={onRetry} disabled={isFetching} className="mt-4">
        {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Retry
      </Button>
    </div>
  );
}

/**
 * A failed "Check again" with rows still on screen — the table stays (and so does the pager
 * below it), the failure is reported here instead. Same wording and shape as the other admin
 * list screens' own "Could not refresh" banner.
 */
function RefreshErrorBanner({
  onRetry,
  isFetching,
}: {
  onRetry: () => void;
  isFetching: boolean;
}) {
  return (
    <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
      <p className="text-sm text-destructive-ink">
        Could not refresh. Showing the last result that loaded.
      </p>
      <Button
        onClick={onRetry}
        variant="outline"
        size="sm"
        disabled={isFetching}
        className="h-7 shrink-0 px-2 text-xs"
      >
        {isFetching && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        Retry
      </Button>
    </div>
  );
}

function EmptyReport({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
      <ShieldCheck className="mx-auto mb-4 h-16 w-16 text-ok" />
      <h3 className="mb-2 text-lg font-semibold">{title}</h3>
      <p className="text-muted-foreground">{detail}</p>
    </div>
  );
}

export default ExpiryMismatchPage;
